import { formatDate } from '@frontend/mwm-ui'
import classNames from 'classnames'
import { useCallback, useMemo, useReducer, useState } from 'react'
import { MdOutlineFlag, MdOutlineStar } from 'react-icons/md'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import CollapseButton from '~/components/Buttons/CollapseButton'
import Filters, { FiltersContent } from '~/components/Filters'
import DateInput from '~/components/Inputs/Basic/DateInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import {
  CustomReviewMlLabelBadgeOption,
  CustomReviewStatusBadgeOption,
  CustomReviewTopicBadgeOption,
} from '~/components/SelectOptions/CustomBadgeOption'
import CustomRatingOption from '~/components/SelectOptions/CustomRatingOption'
import {
  DEFAULT_DATE_RANGE,
  LANGUAGE_BY_LOCALE,
  MAX_DAYS_RANGE,
  ML_LABEL_MAPPING,
  REVIEW_STATUS_MAPPING,
  TODAY,
} from '~/const'
import { useReviewAppVersionsQuery, useReviewLanguagesListQuery } from '~/queries/reviews'
import { useReviewsTopicsQuery } from '~/queries/reviews-topics'
import { getCountries } from '~/utils/countries'
import { includesFilter } from '~/utils/options'

type ActionType =
  | 'appVersions'
  | 'countries'
  | 'start'
  | 'end'
  | 'mlLabels'
  | 'ratings'
  | 'statuses'
  | 'topics'
  | 'reset'
  | 'languages'
  | 'statusFilterType'

type Actions = {
  type: ActionType
  value?: string[]
}

export interface IFiltersState extends Record<string, string[] | string> {
  appVersions: string[]
  countries: string[]
  end: string
  languages: string[]
  mlLabels: string[]
  ratings: string[]
  start: string
  statusFilterType: string
  statuses: string[]
  topics: string[]
}

type ReducerState = (state: IFiltersState, action: Actions) => IFiltersState

export const defaultState: IFiltersState = {
  appVersions: [],
  countries: [],
  end: DEFAULT_DATE_RANGE.end,
  languages: [],
  mlLabels: [],
  ratings: [],
  start: DEFAULT_DATE_RANGE.start,
  statusFilterType: 'overlap',
  statuses: [],
  topics: [],
}

const reducer = (state: IFiltersState, action: Actions) => {
  switch (action.type) {
    case 'appVersions':
      return { ...state, appVersions: action.value || [] }
    case 'countries':
      return { ...state, countries: action.value || [] }
    case 'languages':
      return { ...state, languages: action.value || [] }
    case 'start':
      const startDate = (action.value && action.value[0]) || ''
      return {
        ...state,
        start: startDate,
      }
    case 'end':
      const endDate = (action.value && action.value[0]) || ''
      return {
        ...state,
        end: endDate,
      }
    case 'mlLabels':
      return { ...state, mlLabels: action.value || [] }
    case 'ratings':
      return { ...state, ratings: action.value || [] }
    case 'statuses':
      return { ...state, statuses: action.value || [] }
    case 'topics':
      return { ...state, topics: action.value || [] }
    case 'reset':
      return defaultState
    case 'statusFilterType':
      return { ...state, statusFilterType: action.value?.[0] || '' }
    default:
      return state
  }
}

const getOptionsFromFilters = (filterValues: string[], options: IOption[]): IOption[] => {
  return options.filter(({ value }) => filterValues.includes(value))
}

const formatOptionLabelToStar = (data: unknown) => {
  const { value } = data as IOption
  return (
    <div className="select-input-star-container">
      {value} <MdOutlineStar size={10} />
    </div>
  )
}

interface Props {
  initialState?: IFiltersState
  isOneDeviceTypeOnly: { android: boolean; ios: boolean }
  onFiltersChange: (filter: string, values: string | string[]) => void
  onResetFilters: VoidFunction
  selectedApps: { app_key: string; device_type: string }[]
}

const ReviewsMonitoringFilters = ({
  initialState = defaultState,
  isOneDeviceTypeOnly,
  onFiltersChange,
  onResetFilters,
  selectedApps,
}: Props): ReactElement => {
  /** Local state */

  const [state, dispatch] = useReducer<ReducerState>(reducer, initialState)

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false)

  /** Queries & Mutations */

  const { data: topics, isLoading: areTopicsLoading } = useReviewsTopicsQuery()

  const { data: languages, isLoading: areLanguagesLoading } = useReviewLanguagesListQuery()

  const { data: appVersions, isLoading: areAppVersionsLoading } = useReviewAppVersionsQuery(
    { app_key: selectedApps[0]?.app_key, device_type: selectedApps[0]?.device_type },
    { enabled: selectedApps.length === 1 }
  )

  /** Handlers */

  const toggleCollapseFilters = () => setIsFiltersCollapsed(!isFiltersCollapsed)

  const handleFilterChange = (filter: ActionType, values: string[]) => {
    dispatch({ type: filter, value: values })

    if (filter === 'start' || filter === 'end') {
      onFiltersChange(filter, values[0])
    } else {
      onFiltersChange(filter, values)
    }
  }

  const handleResetAll = useCallback(() => {
    dispatch({ type: 'reset' })
    onResetFilters()
  }, [dispatch, onResetFilters])

  const ratingOptions: IOption[] = useMemo(
    () => [...Array(5)].map((__, index) => ({ label: `${index + 1}`, value: `${index + 1}` })).reverse(),
    []
  )

  const appVersionsOptions: IOption[] =
    appVersions?.map((version) => ({
      label: version,
      value: version,
    })) || []

  const countriesOptions: IOption[] = useMemo(
    () =>
      getCountries().map(({ label, value }) => ({
        icon: <span className={classNames('option-flag-icon fi', { [`fi-${value.toLowerCase()}`]: true })} />,
        label: `${value.toLowerCase()} (${label})`,
        value,
      })),
    []
  )

  const languagesOptions: IOption[] =
    languages?.map((code) => ({
      label: `${code.toLowerCase()} (${LANGUAGE_BY_LOCALE[code] || '?'})`,
      value: code,
    })) || []

  const statusesOptions: IOption[] = Object.entries(REVIEW_STATUS_MAPPING).map(([key, value]) => ({
    label: value,
    value: key,
  }))

  const mlLabelsOptions: IOption[] = Object.entries(ML_LABEL_MAPPING).map(([key, value]) => ({
    label: value,
    value: key,
  }))

  // NOTE: we use the label as value for topics because user sharing url only has the label
  // this is not an issue here as label are uniq and user cannot create a topic with an existing label
  // WARNING bug prone though
  const topicsOptions: IOption[] = topics?.map(({ label }) => ({ label, value: label })) || []

  return (
    <Filters className="reviews-monitoring-filters">
      {!isFiltersCollapsed ? (
        <FiltersContent className="filters-content">
          <div className="filters-content-container">
            <MultiSelectInput
              components={{ Option: CustomRatingOption }}
              formatOptionLabel={formatOptionLabelToStar}
              label="Ratings"
              onChange={(value: unknown) =>
                handleFilterChange(
                  'ratings',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={ratingOptions}
              placeholder="All ratings"
              value={getOptionsFromFilters(state.ratings, ratingOptions)}
            />
            {selectedApps.length === 1 ? (
              <MultiSelectInput
                isLoading={areAppVersionsLoading}
                label="App versions"
                onChange={(value: unknown) =>
                  handleFilterChange(
                    'appVersions',
                    (value as IOption[]).map(({ value }) => value)
                  )
                }
                options={appVersionsOptions}
                placeholder="All App versions"
                value={getOptionsFromFilters(state.appVersions, appVersionsOptions)}
              />
            ) : null}
            <MultiSelectInput
              defaultIcon={<MdOutlineFlag size={16} />}
              disabled={isOneDeviceTypeOnly?.android}
              filterOption={includesFilter}
              label="Countries"
              menuClassName="reviews-monitoring__filter-countries__menu"
              onChange={(value: unknown) =>
                handleFilterChange(
                  'countries',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={countriesOptions}
              placeholder="All Countries"
              value={getOptionsFromFilters(state.countries, countriesOptions)}
            />
            <MultiSelectInput
              disabled={isOneDeviceTypeOnly?.ios}
              filterOption={includesFilter}
              isLoading={areLanguagesLoading}
              label="Languages"
              onChange={(value: unknown) => {
                handleFilterChange(
                  'languages',
                  (value as IOption[]).map(({ value }) => value)
                )
              }}
              options={languagesOptions}
              placeholder="All languages"
              value={getOptionsFromFilters(state.languages, languagesOptions)}
            />
            <MultiSelectInput
              checkboxInitialValue={state.statusFilterType !== 'overlap'}
              className="status-filter"
              components={{ Option: CustomReviewStatusBadgeOption }}
              isCheckboxDisabled={getOptionsFromFilters(state.statuses, statusesOptions).length < 1}
              label="Statuses"
              onChange={(value: unknown) =>
                handleFilterChange(
                  'statuses',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              onCheckboxChanged={(value) =>
                handleFilterChange('statusFilterType', !value ? ['overlap'] : ['not_overlap'])
              }
              options={statusesOptions}
              placeholder="All statuses"
              value={getOptionsFromFilters(state.statuses, statusesOptions)}
              withExcept
            />
            <MultiSelectInput
              components={{ Option: CustomReviewMlLabelBadgeOption }}
              label="ML labels"
              onChange={(value: unknown) =>
                handleFilterChange(
                  'mlLabels',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={mlLabelsOptions}
              placeholder="All ML Labels"
              value={getOptionsFromFilters(state.mlLabels, mlLabelsOptions)}
            />
            <MultiSelectInput
              components={{ Option: CustomReviewTopicBadgeOption }}
              isLoading={areTopicsLoading}
              label="Topics"
              onChange={(value: unknown) =>
                handleFilterChange(
                  'topics',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={topicsOptions}
              placeholder="All Topics"
              value={getOptionsFromFilters(state.topics, topicsOptions)}
            />
            <DateInput
              endDate={state.end}
              maxDate={TODAY}
              maxRangeDays={MAX_DAYS_RANGE}
              onDateRangeChange={(value: unknown) => {
                const [start, end] = value as string[]
                handleFilterChange('start', [formatDate(start, 'YYYY-MM-DD')])
                handleFilterChange('end', [formatDate(end, 'YYYY-MM-DD')])
              }}
              startDate={state.start}
              withFilters
              withRange
            />
          </div>
          <Button className="reset-button" ghost onClick={handleResetAll}>
            Reset all
          </Button>
        </FiltersContent>
      ) : null}
      <CollapseButton isCollapsed={isFiltersCollapsed} onClick={toggleCollapseFilters} />
    </Filters>
  )
}

export default ReviewsMonitoringFilters
